/* eslint-disable camelcase */
import React, { FC, useCallback, useEffect, useMemo, useState, useRef } from "react"
import SyncableModel from "../../../../database/SyncableModel";
import { Appearance, ScrollView } from "react-native"
import { CompositeScreenProps } from "@react-navigation/native"
import { FarmForm as ModelForm, ModelCreateScreen, Text, Button } from "../../../../components"
import { observer } from "mobx-react-lite"
import { useStores } from "../../../../models"
import { Farm } from "../../../../database"
import { translate } from "../../../../i18n"
import _ from "lodash"
import { goBack, navigate } from "../../../../navigators"
import { delay } from "../../../../utils/delay"
import JalaAnalytic from "../../../../utils/jala-analytic"
import { Modal, View, useDisclose } from "native-base";
import { nativeBaseTheme } from "../../../../theme";

export const ConnectionWeakModal: FC<{ colorScheme?: "light" | "dark" }> = observer(function ConnectionWeakModal(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;
  const { isOpen, onClose, onOpen } = useDisclose()
  const {
    appStore: { isOffline }, appStore
  } = useStores();

  useEffect(() => {
    appStore.checkNetworkConnection()
    delay(5)
    if (isOffline) onOpen()
  }, [appStore, isOffline, onOpen])

  const onPress = useCallback(() => {
    onClose()
    goBack('Home.Stack')
  }, [onClose])

  return <Modal
    testID="jala:id/onboard-consultation-modal"
    isOpen={isOpen}
    onClose={onClose}
    animationPreset="slide"
  >
    <View
      alignSelf="stretch"
      marginY="80px"
      marginX="40px"
      padding="16px"
      rounded="8px"
      backgroundColor={colorScheme === 'light' ? nativeBaseTheme.colors.light["50"] : nativeBaseTheme.colors.dark["50"]}
    >
      <Text preset="title1" tx="farmCreateScreen.disconnectedModalTitle" marginBottom="8px" colorScheme={colorScheme} />
      <Text preset="body2" tx="farmCreateScreen.disconnectedModalSubtitle" marginBottom="8px" colorScheme={colorScheme} />
      <View alignItems="center" marginTop="18px" flexDirection="row" justifyContent="flex-end">
        {/* <Button
          testID="jala:id/action-button"
          tx="common.close"
          preset="link"
          colorScheme={colorScheme}
          onPress={onClose}
        /> */}
        <Button
          testID="jala:id/action-button"
          tx="common.close"
          preset="primary"
          colorScheme={colorScheme}
          onPress={onPress}
        />
      </View>
    </View>
  </Modal>;
})

export const FarmCreateScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function FarmCreateScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), route: { params = {} } } = props

  // Pull in one of our MST stores
  const {
    appStore,
    farmStore: modelStore,
  } = useStores();

  const [ model, setModel ] = useState<SyncableModel>(null)
  const scrollViewRef = useRef<ScrollView>(null)

  const initialValues = useMemo<any>(() => {
    const data = (model ? model.toArray() : params)
    return {
      ...data,
    }
  }, [model, params])

  useEffect(() => {
    const farm = appStore.farms.find((m) => m.id === params.id);
    if (farm) {
      const model = Farm.hydrate({ ...farm })
      setModel(model)

      JalaAnalytic.log('Mobile: Visit Setting Farm');
    } else {
      JalaAnalytic.log('Mobile app: create farm visit');
    }
  }, [appStore, params.id])

  const onSuccess = useCallback((success: boolean) => {
    if (modelStore.needRefresh) {
      if (success) {
        const deleting = modelStore.deleting
        if (!model) {
          delay(1).then(() => {
            modelStore.set('loading', true)
            appStore.syncFarmsWithOthers().then(() => {
              appStore.getFarms(false).then(async () => {
                modelStore.set('needRefresh', false);
                modelStore.set('pushIdQueue', {})
                const latestFarm = _.maxBy(appStore.farms, 'created_at')
                if (latestFarm) await appStore.set('farmId', latestFarm.id)
                if (appStore.farmStore) {
                  appStore.farmStore.initWithLastBatchAndWaterQualityMultiMeasurements();
                  goBack('Home.Stack')
                  navigate('App.Stack', 'Home.Stack', { screen: 'Home.Farm.Pond.List' })
                }
                modelStore.set('loading', false)
              });
            })
          })
          return false
        } else {
          appStore.getFarms(true, () => {
            modelStore.set('needRefresh', false);
            modelStore.set('pushIdQueue', {})
          });
        }
        if (deleting) {
          navigate('Home.Stack', 'App.Stack')
          return false
        }
      } else {
        modelStore.set('needRefresh', false);
        modelStore.set('pushIdQueue', {})
      }
    }
    return success;
  }, [appStore, model, modelStore])

  return (<>
    <ModelCreateScreen
      colorScheme={colorScheme}
      ModelForm={ModelForm}
      initialValues={initialValues}
      modelStore={modelStore}
      model={model}
      onSuccess={onSuccess}
      deleteModalSubtitle={translate('farmForm.deleteSubtitle')}
      saveTx={!model ? "farmCreateScreen.title" : undefined}
      scrollViewRef={scrollViewRef}
    />
    <ConnectionWeakModal />
  </>)
})
